
import * as z from "zod";

export const createFormSchema = (t: (key: string) => string) => z.object({
  firstName: z.string().min(2, { message: t('clients.firstNameMinLength') }),
  lastName: z.string().min(2, { message: t('clients.lastNameMinLength') }),
  email: z.string().email({ message: t('clients.invalidEmail') }).nullable().optional().or(z.literal("")).transform((val) => {
    if (!val || val === "") return null;
    return val;
  }),
  birthDate: z.string().optional().refine((val) => {
    if (!val) return true;
    const date = new Date(val);
    const today = new Date();
    const minDate = new Date();
    minDate.setFullYear(today.getFullYear() - 100);
    const maxDate = new Date();
    maxDate.setFullYear(today.getFullYear() - 16);
    
    return date >= minDate && date <= maxDate;
  }, { message: t('clients.birthDateAge') }),
  gender: z.enum(["male", "female", "other"]),
  weightKg: z.string().nullable().optional().transform((val) => {
    if (!val || val === "" || val === null) return undefined;
    const num = Number(val);
    return isNaN(num) ? undefined : num;
  }).refine((val) => val === undefined || (val >= 1 && val <= 999), { 
    message: t('clients.weightRange') 
  }),
  heightCm: z.string().nullable().optional().transform((val) => {
    if (!val || val === "" || val === null) return undefined;
    const num = Number(val);
    return isNaN(num) ? undefined : num;
  }).refine((val) => val === undefined || (val >= 1 && val <= 999), { 
    message: t('clients.heightRange') 
  }),
  phone: z.string().min(1, { message: t('clients.phoneRequired') }),
  address: z.string().optional(),
  notes: z.string().optional(),
  paymentStatus: z.enum(["paid", "unpaid", "partial", "pending"]).optional(),
  subscriptions: z.array(
    z.object({
      workoutTypeId: z.string(),
      sessionsPerMonth: z.string().min(1, { message: t('clients.sessionsPerMonthRequired') }).refine((val) => {
        const num = Number(val);
        return !isNaN(num) && num >= 1 && num <= 30;
      }, { message: t('clients.sessionsPerMonthValidation') }),
      totalPrice: z.string().min(1, { message: t('clients.totalPriceRequired') }).refine((val) => {
        const num = Number(val);
        return !isNaN(num) && num > 0;
      }, { message: t('clients.totalPriceValidation') })
    })
  )
});

export type FormData = {
  firstName: string;
  lastName: string;
  email?: string | null;
  birthDate?: string;
  gender: "male" | "female" | "other";
  weightKg?: string;
  heightCm?: string;
  phone: string;
  address?: string;
  notes?: string;
  paymentStatus?: "paid" | "unpaid" | "partial" | "pending";
  subscriptions: {
    workoutTypeId: string;
    sessionsPerMonth: string;
    totalPrice: string;
  }[];
};

export const getDefaultFormValues = () => ({
  firstName: "",
  lastName: "",
  email: "",
  birthDate: "",
  gender: "male" as const,
  weightKg: "",
  heightCm: "",
  phone: "",
  address: "",
  notes: "",
  paymentStatus: "unpaid" as const,
  subscriptions: []
});
